import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  type DragEndEvent,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { PointTypeItem, PointTypeSet } from '../../types/pointItemNote';
import PointTypeEmojiPicker from './PointTypeEmojiPicker';
import PointTypeIcon from './PointTypeIcon';
import UnsavedCloseConfirm from './UnsavedCloseConfirm';

type TabKey = 'positive' | 'negative';

type Props = {
  open: boolean;
  pointSet: PointTypeSet;
  onClose: () => void;
  onSave: (pointSet: PointTypeSet) => void;
};

function newId(): string {
  return `pt-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const SortablePointRow: React.FC<{
  item: PointTypeItem;
  isEditing: boolean;
  editPoints: number;
  editMemo: string;
  editEmoji: string | undefined;
  emojiPickerOpen: boolean;
  onEditPoints: (n: number) => void;
  onEditMemo: (s: string) => void;
  onEditEmoji: (emoji: string | undefined) => void;
  onToggleEmojiPicker: () => void;
  onCloseEmojiPicker: () => void;
  onStartEdit: () => void;
  onConfirmEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onPickEmojiForItem: (emoji: string | undefined) => void;
}> = ({
  item,
  isEditing,
  editPoints,
  editMemo,
  editEmoji,
  emojiPickerOpen,
  onEditPoints,
  onEditMemo,
  onEditEmoji,
  onToggleEmojiPicker,
  onCloseEmojiPicker,
  onStartEdit,
  onConfirmEdit,
  onCancelEdit,
  onDelete,
  onPickEmojiForItem,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };
  const iconBtnRef = useRef<HTMLButtonElement>(null);

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style} className="cp-pin-set-row cp-pin-set-row--editing">
        <div className="cp-pin-set-row-main">
          <div className="cp-pin-set-gem-wrap">
            <button
              ref={iconBtnRef}
              type="button"
              className="cp-pin-set-gem-btn"
              onClick={onToggleEmojiPicker}
              title="이모지 선택"
              aria-label="이모지 선택"
              aria-expanded={emojiPickerOpen}
            >
              <PointTypeIcon
                positive={item.category === 'positive'}
                emoji={editEmoji}
                size={24}
              />
            </button>
            <PointTypeEmojiPicker
              open={emojiPickerOpen}
              anchorRef={iconBtnRef}
              onClose={onCloseEmojiPicker}
              onSelect={onEditEmoji}
            />
          </div>
          <input
            type="number"
            min={0}
            className="cp-pin-set-points-input"
            value={editPoints}
            onChange={(e) => onEditPoints(Number(e.target.value) || 0)}
            placeholder="포인트"
            aria-label="포인트"
          />
          <input
            type="text"
            className="cp-pin-set-memo-input"
            value={editMemo}
            onChange={(e) => onEditMemo(e.target.value)}
            placeholder="메모를 입력하세요"
            aria-label="메모"
          />
        </div>
        <div className="cp-pin-set-edit-actions">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={onCancelEdit}>
            닫기
          </button>
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--primary" onClick={onConfirmEdit}>
            확인
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={setNodeRef} style={style} className="cp-pin-set-row">
      <div className="cp-pin-set-row-main">
        <div className="cp-pin-set-gem-wrap">
          <button
            ref={iconBtnRef}
            type="button"
            className="cp-pin-set-gem-btn"
            onClick={onToggleEmojiPicker}
            title="이모지 선택"
            aria-label="이모지 선택"
            aria-expanded={emojiPickerOpen}
          >
            <PointTypeIcon positive={item.category === 'positive'} emoji={item.emoji} />
          </button>
          <PointTypeEmojiPicker
            open={emojiPickerOpen}
            anchorRef={iconBtnRef}
            onClose={onCloseEmojiPicker}
            onSelect={onPickEmojiForItem}
          />
        </div>
        <span className="cp-pin-set-points">{item.points}점</span>
        <span className="cp-pin-set-memo">{item.memo || '이름 없음'}</span>
      </div>
      <div className="cp-pin-set-row-actions">
        <button type="button" className="cp-pin-set-icon-btn" onClick={onStartEdit} title="수정" aria-label="수정">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button type="button" className="cp-pin-set-icon-btn" onClick={onDelete} title="삭제" aria-label="삭제">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path
              d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          className="cp-pin-set-icon-btn cp-pin-set-drag"
          title="순서 조정"
          aria-label="순서 조정"
          {...attributes}
          {...listeners}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

const PointTypeSetModal: React.FC<Props> = ({ open, pointSet, onClose, onSave }) => {
  const [tab, setTab] = useState<TabKey>('positive');
  const [positiveTypes, setPositiveTypes] = useState<PointTypeItem[]>([]);
  const [negativeTypes, setNegativeTypes] = useState<PointTypeItem[]>([]);
  const [levelUpPoints, setLevelUpPoints] = useState(50);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPoints, setEditPoints] = useState(1);
  const [editMemo, setEditMemo] = useState('');
  const [editEmoji, setEditEmoji] = useState<string | undefined>(undefined);
  const [emojiPickerForId, setEmojiPickerForId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  useEffect(() => {
    if (!open) return;
    setTab('positive');
    setPositiveTypes(pointSet.positiveTypes.map((t) => ({ ...t })));
    setNegativeTypes(pointSet.negativeTypes.map((t) => ({ ...t })));
    setLevelUpPoints(pointSet.levelUpPoints || 50);
    setEditingId(null);
    setEditEmoji(undefined);
    setEmojiPickerForId(null);
    setDeleteId(null);
    setConfirmClose(false);
  }, [open, pointSet]);

  const baseline = useMemo(
    () =>
      JSON.stringify({
        positiveTypes: pointSet.positiveTypes,
        negativeTypes: pointSet.negativeTypes,
        levelUpPoints: pointSet.levelUpPoints || 50,
      }),
    [pointSet]
  );

  const currentSnapshot = useMemo(
    () =>
      JSON.stringify({
        positiveTypes,
        negativeTypes,
        levelUpPoints,
      }),
    [positiveTypes, negativeTypes, levelUpPoints]
  );

  const dirty = open && (baseline !== currentSnapshot || editingId != null);

  const current = tab === 'positive' ? positiveTypes : negativeTypes;
  const setCurrent = tab === 'positive' ? setPositiveTypes : setNegativeTypes;

  const ids = useMemo(() => current.map((t) => t.id), [current]);

  if (!open) return null;

  const handleClose = () => {
    if (dirty) setConfirmClose(true);
    else onClose();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCurrent((prev) => {
      const oldIndex = prev.findIndex((t) => t.id === active.id);
      const newIndex = prev.findIndex((t) => t.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleAdd = () => {
    if (editingId) return;
    const now = new Date().toISOString();
    const item: PointTypeItem = {
      id: newId(),
      points: 1,
      memo: '',
      category: tab,
      createdAt: now,
      updatedAt: now,
    };
    setCurrent((prev) => [...prev, item]);
    setEditingId(item.id);
    setEditPoints(1);
    setEditMemo('');
    setEditEmoji(undefined);
    setEmojiPickerForId(null);
  };

  const startEdit = (item: PointTypeItem) => {
    setEditingId(item.id);
    setEditPoints(item.points);
    setEditMemo(item.memo);
    setEditEmoji(item.emoji);
    setEmojiPickerForId(null);
  };

  const confirmEdit = () => {
    if (!editingId) return;
    const now = new Date().toISOString();
    setCurrent((prev) =>
      prev.map((t) =>
        t.id === editingId
          ? {
              ...t,
              points: editPoints,
              memo: editMemo.trim(),
              emoji: editEmoji?.trim() || undefined,
              updatedAt: now,
            }
          : t
      )
    );
    setEditingId(null);
    setEmojiPickerForId(null);
  };

  const cancelEdit = () => {
    if (!editingId) return;
    const target = current.find((t) => t.id === editingId);
    if (target && !target.memo.trim() && target.points === 1 && !target.emoji) {
      setCurrent((prev) => prev.filter((t) => t.id !== editingId));
    }
    setEditingId(null);
    setEmojiPickerForId(null);
  };

  const applyEmojiToItem = (itemId: string, emoji: string | undefined) => {
    const now = new Date().toISOString();
    setCurrent((prev) =>
      prev.map((t) =>
        t.id === itemId ? { ...t, emoji: emoji?.trim() || undefined, updatedAt: now } : t
      )
    );
  };

  const executeDelete = () => {
    if (!deleteId) return;
    setCurrent((prev) => prev.filter((t) => t.id !== deleteId));
    if (editingId === deleteId) setEditingId(null);
    if (emojiPickerForId === deleteId) setEmojiPickerForId(null);
    setDeleteId(null);
  };

  const handleSave = () => {
    const now = new Date().toISOString();
    const applyEdit = (list: PointTypeItem[]) =>
      editingId
        ? list.map((t) =>
            t.id === editingId
              ? {
                  ...t,
                  points: editPoints,
                  memo: editMemo.trim(),
                  emoji: editEmoji?.trim() || undefined,
                  updatedAt: now,
                }
              : t
          )
        : list;
    onSave({
      positiveTypes: applyEdit(positiveTypes),
      negativeTypes: applyEdit(negativeTypes),
      levelUpPoints: Math.max(1, levelUpPoints || 50),
    });
    onClose();
  };

  return createPortal(
    <div className="cp-apn-modal-overlay" role="presentation" onClick={handleClose}>
      <div
        className="cp-apn-modal cp-pin-settings-modal"
        data-tutorial-pin="point-set-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-pin-point-set-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="cp-pin-modal-header">
          <h2 id="cp-pin-point-set-title">포인트 타입별 세트 설정</h2>
          <button type="button" className="cp-pin-modal-close" onClick={handleClose} aria-label="닫기">
            ×
          </button>
        </header>

        <div
          className="cp-pin-bar-tabs cp-pin-bar-tabs--2"
          role="tablist"
          data-tutorial-pin="point-set-tabs"
        >
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'positive'}
            className={`cp-pin-bar-tab${tab === 'positive' ? ' is-active' : ''}`}
            onClick={() => {
              if (editingId) return;
              setTab('positive');
              setEmojiPickerForId(null);
            }}
          >
            긍정행동 포인트
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'negative'}
            className={`cp-pin-bar-tab${tab === 'negative' ? ' is-active' : ''}`}
            onClick={() => {
              if (editingId) return;
              setTab('negative');
              setEmojiPickerForId(null);
            }}
          >
            행동체크 포인트
          </button>
        </div>

        <div className="cp-pin-modal-body" data-tutorial-pin="point-set-body">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="cp-pin-set-list">
                {current.map((item) => (
                  <SortablePointRow
                    key={item.id}
                    item={item}
                    isEditing={editingId === item.id}
                    editPoints={editPoints}
                    editMemo={editMemo}
                    editEmoji={editEmoji}
                    emojiPickerOpen={emojiPickerForId === item.id}
                    onEditPoints={setEditPoints}
                    onEditMemo={setEditMemo}
                    onEditEmoji={setEditEmoji}
                    onToggleEmojiPicker={() =>
                      setEmojiPickerForId((prev) => (prev === item.id ? null : item.id))
                    }
                    onCloseEmojiPicker={() => setEmojiPickerForId(null)}
                    onStartEdit={() => startEdit(item)}
                    onConfirmEdit={confirmEdit}
                    onCancelEdit={cancelEdit}
                    onDelete={() => setDeleteId(item.id)}
                    onPickEmojiForItem={(emoji) => applyEmojiToItem(item.id, emoji)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            className="cp-pin-add-dashed"
            data-tutorial-pin="point-set-add"
            disabled={editingId != null}
            onClick={handleAdd}
          >
            <span className="cp-pin-add-dashed-icon">+</span>
            새 세트 추가
          </button>
        </div>

        <footer className="cp-pin-modal-footer" data-tutorial-pin="point-set-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={handleClose}>
            닫기
          </button>
          <button
            type="button"
            className="cp-pin-modal-btn cp-pin-modal-btn--primary"
            data-tutorial-pin="point-set-save"
            onClick={handleSave}
          >
            저장
          </button>
        </footer>

        {deleteId && (
          <div className="cp-pin-confirm-overlay" role="presentation" onClick={() => setDeleteId(null)}>
            <div className="cp-pin-confirm" role="dialog" onClick={(e) => e.stopPropagation()}>
              <p>이 포인트 블럭을 삭제할까요?</p>
              <div className="cp-pin-confirm-actions">
                <button
                  type="button"
                  className="cp-pin-modal-btn cp-pin-modal-btn--secondary"
                  onClick={() => setDeleteId(null)}
                >
                  취소
                </button>
                <button type="button" className="cp-apn-btn cp-apn-btn-danger" onClick={executeDelete}>
                  삭제
                </button>
              </div>
            </div>
          </div>
        )}

        <UnsavedCloseConfirm
          open={confirmClose}
          onCancel={() => setConfirmClose(false)}
          onConfirm={() => {
            setConfirmClose(false);
            onClose();
          }}
        />
      </div>
    </div>,
    document.body
  );
};

export default PointTypeSetModal;
